import axios from 'axios';
import logger from '../utils/logger';

const API_BASE_URL = process.env.REACT_APP_API_URL || '/api';
const CLIENT_API_KEY = process.env.REACT_APP_CLIENT_API_KEY;


export interface Action {
  id?: number;
  actionName: string;
  description?: string;
  created_at?: string;
  updated_at?: string;
}

export interface Object {
  id?: number;
  name: string;
  description: string;
  created_at?: string;
  updated_at?: string;
}

export interface Modifier {
  id?: number;
  name: string;
  created_at?: string;
  updated_at?: string;
}

export interface ActionObjectRelation {
  action_id: number;
  object_id: number;
  action_name?: string;
  object_name?: string;
  created_at?: string;
}

export interface ActionObjectPair {
  action: string;
  object: string;
  modifier?: string;
  structure?: string;
}

export interface ClassificationResponse {
  intents: ActionObjectPair[];
  transcription?: string;
}

export interface SessionEntry {
  id: number;
  text: string;
  context?: string;
  intents: ActionObjectPair[];
  timestamp: Date;
}

export interface Session {
  id?: number;
  sessionId: string;
  status: 'active' | 'closed';
  message?: string;
  ip_address?: string;
  created_at?: string;
  closed_at?: string;
}

export interface ConversationTurn {
  id?: number;
  sessionId: string;
  userInput: string;
  classifiedIntents: string; // JSON string of classified intents
  context?: string;
  openai_prompt_tokens?: number;
  openai_completion_tokens?: number;
  openai_total_tokens?: number;
  openai_cost?: number;
  timestamp?: string;
  feedback_rating?: 'correct' | 'incorrect' | 'mixed' | null;
  feedback_comment?: string;
  feedback_timestamp?: string;
}

export interface ConversationHistory {
  sessionId: string;
  history: ConversationTurn[];
}

export interface AuthUser {
  id: number;
  username: string;
  email: string;
  name: string | null;
  institution: string | null;
  role: 'user' | 'admin';
}

export interface AdminUser extends AuthUser {
  is_active: boolean;
  last_login_at: string | null;
  created_at: string;
  updated_at: string;
  memory_count: number;
}

export interface UserMemory {
  id: number;
  user_id: number;
  content: string;
  feedback: 'correct' | 'incorrect' | 'mixed' | null;
  source_turn_id: number | null;
  created_at: string;
}

export interface Structure {
  id?: number;
  structure_name: string;
  alternate_phrases: string;
  created_at?: string;
  updated_at?: string;
}

export interface AuthResponse {
  message: string;
  token: string;
  user: AuthUser;
}

class ApiService {
  private axios = axios.create({
    baseURL: API_BASE_URL,
    timeout: 30000,
  });

  constructor() {
    // Add authentication header for client requests
    logger.info('API Service initialization:');
    logger.info('API_BASE_URL:', API_BASE_URL);
    logger.info('CLIENT_API_KEY:', CLIENT_API_KEY ? 'Present' : 'Missing');
    
    if (CLIENT_API_KEY) {
      this.axios.defaults.headers.common['x-api-key'] = CLIENT_API_KEY;
      logger.info('API key header set successfully');
    } else {
      logger.warn('CLIENT_API_KEY is missing - API requests may fail');
    }
    
    // Add request interceptor for debugging
    this.axios.interceptors.request.use(
      (config) => {
        logger.info('API Request:', {
          method: config.method?.toUpperCase(),
          url: config.url,
          baseURL: config.baseURL,
          timeout: config.timeout
        });
        return config;
      },
      (error) => {
        logger.error('API Request Error:', error);
        return Promise.reject(error);
      }
    );
    
    // Add response interceptor for debugging
    this.axios.interceptors.response.use(
      (response) => {
        logger.info('API Response:', {
          status: response.status,
          statusText: response.statusText,
          url: response.config.url
        });
        return response;
      },
      (error) => {
        logger.error('API Response Error:', {
          message: error.message,
          status: error.response?.status,
          statusText: error.response?.statusText,
          url: error.config?.url
        });
        return Promise.reject(error);
      }
    );
  }


  // Update the JWT auth token on the axios instance
  setAuthToken(token: string | null) {
    if (token) {
      this.axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    } else {
      delete this.axios.defaults.headers.common['Authorization'];
    }
  }

  // Auth methods
  async login(login: string, password: string): Promise<AuthResponse> {
    const response = await this.axios.post('/auth/login', { login, password });
    return response.data;
  }

  async register(
    username: string,
    email: string,
    password: string,
    name?: string,
    institution?: string
  ): Promise<AuthResponse> {
    const response = await this.axios.post('/auth/register', { username, email, password, name, institution });
    return response.data;
  }

  async getMe(): Promise<AuthUser> {
    const response = await this.axios.get('/auth/me');
    return response.data;
  }

  // Session management
  async createSession(): Promise<Session> {
    const response = await this.axios.post('/sessions');
    return response.data;
  }

  async closeSession(sessionId: string): Promise<{ message: string; sessionId: string }> {
    const response = await this.axios.post('/sessions/close', { sessionId });
    return response.data;
  }

  async getAllSessions(startDate?: string, endDate?: string, feedbackRating?: 'correct' | 'incorrect' | 'mixed'): Promise<{
    sessions: Session[];
    feedbackSummary: { correct: number; incorrect: number; mixed: number };
  }> {
    const params: any = {};
    if (startDate) params.startDate = startDate;
    if (endDate) params.endDate = endDate;
    if (feedbackRating) params.feedbackRating = feedbackRating;
    
    const response = await this.axios.get('/sessions', { params });
    return response.data;
  }

  async getConversationHistory(sessionId: string): Promise<ConversationHistory> {
    const response = await this.axios.get(`/sessions/${sessionId}/history`);
    return response.data;
  }

  // Text classification - public endpoint, but client gets priority
  async classifyText(text: string, sessionId: string, context?: string, provider: 'openai' | 'ollama' = 'openai', model?: string): Promise<ClassificationResponse> {
    const response = await this.axios.post('/classify/text', { 
      text, 
      sessionId, 
      context,
      provider,
      model
    });
    return response.data;
  }

  // Voice classification - public endpoint, but client gets priority
  async classifyVoice(audioBlob: Blob, sessionId: string, provider: 'openai' | 'ollama' = 'openai', model?: string): Promise<ClassificationResponse> {
    const formData = new FormData();
    formData.append('audio', audioBlob, 'audio.wav');
    formData.append('sessionId', sessionId);
    formData.append('provider', provider);
    if (model) {
      formData.append('model', model);
    }
    
    const response = await this.axios.post('/classify/voice', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  }


  // Action Management Methods
  // Get all actions
  async getActions(): Promise<Action[]> {
    logger.info('Getting actions...');
    try {
      const response = await this.axios.get('/actions');
      logger.info('Actions response:', response.status, response.data?.length || 0, 'actions');
      return response.data;
    } catch (error: any) {
      logger.error('Get actions error:', {
        message: error.message,
        status: error.response?.status,
        statusText: error.response?.statusText,
        data: error.response?.data,
        config: {
          url: error.config?.url,
          baseURL: error.config?.baseURL,
          headers: error.config?.headers
        }
      });
      throw error;
    }
  }

  // Create action
  async createAction(action: Omit<Action, 'id' | 'created_at' | 'updated_at'>): Promise<{ message: string; id: number }> {
    const response = await this.axios.post('/actions', action);
    return response.data;
  }

  // Update action
  async updateAction(id: number, action: Omit<Action, 'id' | 'created_at' | 'updated_at'>): Promise<{ message: string }> {
    const response = await this.axios.put(`/actions/${id}`, action);
    return response.data;
  }

  // Delete action
  async deleteAction(id: number): Promise<{ message: string }> {
    const response = await this.axios.delete(`/actions/${id}`);
    return response.data;
  }

  // Upload Actions CSV
  async uploadActionsCSV(file: File): Promise<{ message: string; insertedCount: number; duplicates: string[]; errors: string[]; totalProcessed: number }> {
    const formData = new FormData();
    formData.append('file', file);
    
    logger.info('Uploading file:', {
      name: file.name,
      size: file.size,
      type: file.type,
      lastModified: file.lastModified
    });
    
    const response = await this.axios.post('/actions/upload', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
      timeout: 60000, // Increase timeout for file uploads
    });
    return response.data;
  }

  // Download Actions CSV
  async downloadActionsCSV(): Promise<Blob> {
    const response = await this.axios.get('/actions/download', {
      responseType: 'blob',
    });
    return response.data;
  }

  // Object Management Methods
  // Get all objects
  async getObjects(): Promise<Object[]> {
    const response = await this.axios.get('/objects');
    return response.data;
  }

  // Create object
  async createObject(object: Omit<Object, 'id' | 'created_at' | 'updated_at'>): Promise<{ message: string; id: number }> {
    const response = await this.axios.post('/objects', object);
    return response.data;
  }

  // Update object
  async updateObject(id: number, object: Omit<Object, 'id' | 'created_at' | 'updated_at'>): Promise<{ message: string }> {
    const response = await this.axios.put(`/objects/${id}`, object);
    return response.data;
  }

  // Delete object
  async deleteObject(id: number): Promise<{ message: string }> {
    const response = await this.axios.delete(`/objects/${id}`);
    return response.data;
  }

  // Upload Objects CSV
  async uploadObjectsCSV(file: File): Promise<{ message: string; processedCount: number; totalCount?: number; duplicates?: string[]; errors?: string[] }> {
    const formData = new FormData();
    formData.append('file', file);
    
    logger.info('Uploading objects file:', {
      name: file.name,
      size: file.size,
      type: file.type,
      lastModified: file.lastModified
    });
    
    const response = await this.axios.post('/objects/upload', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
      timeout: 60000, // Increase timeout for file uploads
    });
    return response.data;
  }


  // Download Objects CSV
  async downloadObjectsCSV(): Promise<Blob> {
    logger.info('Requesting objects CSV download...');
    logger.info('API Base URL:', this.axios.defaults.baseURL);
    logger.info('API Headers:', this.axios.defaults.headers);
    
    try {
      const response = await this.axios.get('/objects/download', {
        responseType: 'blob',
        timeout: 30000, // 30 second timeout for downloads
        headers: {
          'Accept': 'text/csv, application/json, */*'
        }
      });
      
      logger.info('Objects download response:', {
        status: response.status,
        statusText: response.statusText,
        headers: response.headers,
        dataSize: response.data.size
      });
      
      return response.data;
    } catch (error: any) {
      logger.error('Download API error details:', {
        message: error.message,
        status: error.response?.status,
        statusText: error.response?.statusText,
        data: error.response?.data,
        headers: error.response?.headers,
        config: error.config
      });
      
      // Handle case where server returns JSON error but we expected blob
      if (error.response && error.response.status >= 400) {
        // Try to read the error as text if it's a blob
        if (error.response.data instanceof Blob) {
          try {
            const errorText = await error.response.data.text();
            logger.info('Error response text:', errorText);
            
            // Try to parse as JSON
            try {
              const errorJson = JSON.parse(errorText);
              throw new Error(errorJson.error || errorJson.message || 'Server error');
            } catch (parseError) {
              throw new Error(errorText || 'Unknown server error');
            }
          } catch (blobError) {
            logger.error('Failed to read error blob:', blobError);
            throw new Error(`Server error (${error.response.status}): ${error.response.statusText}`);
          }
        } else {
          // Error data is already in readable format
          const errorMessage = error.response.data?.error || error.response.data?.message || error.response.statusText;
          throw new Error(errorMessage);
        }
      }
      
      // Re-throw the original error if not a server error
      throw error;
    }
  }

  // Modifier Management Methods
  // Get all modifiers
  async getModifiers(): Promise<Modifier[]> {
    const response = await this.axios.get('/modifiers');
    return response.data;
  }

  // Get modifier by ID
  async getModifierById(id: number): Promise<Modifier> {
    const response = await this.axios.get(`/modifiers/${id}`);
    return response.data;
  }

  // Create modifier
  async createModifier(modifier: Omit<Modifier, 'id' | 'created_at' | 'updated_at'>): Promise<{ message: string; id: number }> {
    const response = await this.axios.post('/modifiers', modifier);
    return response.data;
  }

  // Update modifier
  async updateModifier(id: number, modifier: Omit<Modifier, 'id' | 'created_at' | 'updated_at'>): Promise<{ message: string }> {
    const response = await this.axios.put(`/modifiers/${id}`, modifier);
    return response.data;
  }

  // Delete modifier
  async deleteModifier(id: number): Promise<{ message: string }> {
    const response = await this.axios.delete(`/modifiers/${id}`);
    return response.data;
  }

  // Upload Modifiers CSV
  async uploadModifiersCSV(file: File): Promise<{ 
    message: string; 
    processedCount: number; 
    totalCount: number;
    duplicates?: string[];
    errors?: string[];
  }> {
    const formData = new FormData();
    formData.append('file', file);
    
    const response = await this.axios.post('/modifiers/upload', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  }

  // Download Modifiers CSV
  async downloadModifiersCSV(): Promise<Blob> {
    const response = await this.axios.get('/modifiers/download', {
      responseType: 'blob',
    });
    return response.data;
  }

  // Action-Object Relations Management Methods
  // Get all relations
  async getRelations(): Promise<ActionObjectRelation[]> {
    const response = await this.axios.get('/relations');
    return response.data;
  }

  // Create relation
  async createRelation(relation: { action_id: number; object_id: number }): Promise<{ message: string }> {
    const response = await this.axios.post('/relations', relation);
    return response.data;
  }

  // Delete relation
  async deleteRelation(action_id: number, object_id: number): Promise<{ message: string }> {
    const response = await this.axios.delete(`/relations/${action_id}/${object_id}`);
    return response.data;
  }

  // Upload Relations CSV
  async uploadRelationsCSV(file: File): Promise<{ message: string; processed: number; inserted: number }> {
    const formData = new FormData();
    formData.append('file', file);
    
    const response = await this.axios.post('/relations/upload', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  }

  // Download Relations CSV
  async downloadRelationsCSV(): Promise<Blob> {
    const response = await this.axios.get('/relations/download', {
      responseType: 'blob',
    });
    return response.data;
  }

  // Analytics Methods
  async getTokenUsageAnalytics(period: 'day' | 'week' | 'month' | 'year', startDate?: string, endDate?: string): Promise<any> {
    const params: any = { period };
    if (startDate) params.startDate = startDate;
    if (endDate) params.endDate = endDate;
    
    const response = await this.axios.get('/analytics/token-usage', { params });
    return response.data;
  }

  // Ollama Methods
  async getOllamaModels(): Promise<string[]> {
    try {
      logger.info('Fetching Ollama models...');
      const response = await this.axios.get('/ollama/models');
      logger.info('Ollama models response:', response.data);
      return response.data.models || [];
    } catch (error: any) {
      logger.error('Error fetching Ollama models:', error);
      // Return empty array if Ollama is not available
      return [];
    }
  }

  // Validate OpenAI model
  async validateOpenAIModel(model: string): Promise<{ valid: boolean; message?: string }> {
    try {
      logger.info('Validating OpenAI model:', model);
      const response = await this.axios.post('/openai/validate-model', { model });
      logger.info('Model validation response:', response.data);
      return response.data;
    } catch (error: any) {
      logger.error('Error validating OpenAI model:', error);
      return {
        valid: false,
        message: error.response?.data?.message || error.message || 'Failed to validate model'
      };
    }
  }

  // Cache Management Methods
  async getCachedClassifications(limit: number = 100, offset: number = 0): Promise<{ rows: any[], total: number }> {
    const response = await this.axios.get('/cache', { params: { limit, offset } });
    return response.data;
  }

  async updateCachedClassification(id: number, inputText: string, context: string | undefined, classifiedResult: any): Promise<{ message: string }> {
    const response = await this.axios.put(`/cache/${id}`, {
      input_text: inputText,
      context,
      classified_result: classifiedResult
    });
    return response.data;
  }

  async deleteCachedClassification(id: number): Promise<{ message: string }> {
    const response = await this.axios.delete(`/cache/${id}`);
    return response.data;
  }

  async clearAllCache(): Promise<{ message: string; deletedCount: number }> {
    const response = await this.axios.delete('/cache');
    return response.data;
  }

  // Settings Management Methods
  async getSettings(): Promise<{ ai_provider: 'openai' | 'ollama'; use_query_embedding: boolean; use_cache: boolean; use_memory: boolean }> {
    const response = await this.axios.get('/settings');
    return response.data;
  }

  async updateSettings(ai_provider: 'openai' | 'ollama', use_query_embedding: boolean, use_cache: boolean, use_memory: boolean): Promise<{ message: string; settings: any }> {
    const response = await this.axios.put('/settings', {
      ai_provider,
      use_query_embedding,
      use_cache,
      use_memory
    });
    return response.data;
  }

  // ─── Admin Methods ────────────────────────────────────────────────────────

  async adminGetUsers(): Promise<AdminUser[]> {
    const response = await this.axios.get('/admin/users');
    return response.data;
  }

  async adminUpdateUser(
    id: number,
    fields: { name?: string; email?: string; institution?: string; role?: 'user' | 'admin'; is_active?: boolean }
  ): Promise<{ message: string; user: AdminUser }> {
    const response = await this.axios.put(`/admin/users/${id}`, fields);
    return response.data;
  }

  async adminDeleteUser(id: number): Promise<{ message: string }> {
    const response = await this.axios.delete(`/admin/users/${id}`);
    return response.data;
  }

  async adminGetUserMemories(userId: number): Promise<UserMemory[]> {
    const response = await this.axios.get(`/admin/users/${userId}/memories`);
    return response.data;
  }

  async adminDeleteMemory(memoryId: number): Promise<{ message: string }> {
    const response = await this.axios.delete(`/admin/memories/${memoryId}`);
    return response.data;
  }

  async adminDeleteAllUserMemories(userId: number): Promise<{ message: string; deletedCount: number }> {
    const response = await this.axios.delete(`/admin/users/${userId}/memories`);
    return response.data;
  }

  // ─── Structure Management Methods ─────────────────────────────────────────

  async getStructures(): Promise<Structure[]> {
    const response = await this.axios.get('/structures');
    return response.data;
  }

  async getStructure(id: number): Promise<Structure> {
    const response = await this.axios.get(`/structures/${id}`);
    return response.data;
  }

  async createStructure(structure_name: string, alternate_phrases: string): Promise<{ id: number; message: string }> {
    const response = await this.axios.post('/structures', { structure_name, alternate_phrases });
    return response.data;
  }

  async updateStructure(id: number, structure_name: string, alternate_phrases: string): Promise<{ message: string }> {
    const response = await this.axios.put(`/structures/${id}`, { structure_name, alternate_phrases });
    return response.data;
  }

  async deleteStructure(id: number): Promise<{ message: string }> {
    const response = await this.axios.delete(`/structures/${id}`);
    return response.data;
  }

  async uploadStructures(file: File): Promise<{ message: string; insertedCount: number; duplicates: string[]; errors: string[]; totalProcessed: number }> {
    const formData = new FormData();
    formData.append('file', file);
    const response = await this.axios.post('/structures/upload', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data;
  }

  async downloadStructures(): Promise<Blob> {
    const response = await this.axios.get('/structures/download', {
      responseType: 'blob',
    });
    return response.data;
  }

  async generateStructureEmbeddings(): Promise<{ message: string; success: number; failed: number; total: number }> {
    const response = await this.axios.post('/structures/generate-embeddings');
    return response.data;
  }

  // Feedback Methods
  async submitFeedback(conversationTurnId: number, rating: 'correct' | 'incorrect' | 'mixed', comment?: string): Promise<{ message: string }> {
    const response = await this.axios.post('/feedback', {
      conversationTurnId,
      rating,
      comment
    });
    return response.data;
  }
}

export const apiService = new ApiService();
